import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, Loader2, Plus, RefreshCw } from "lucide-react";
import { API } from "../App";
import { PortalHero } from "../components/portal/PortalHero";
import { PortalStats } from "../components/portal/PortalStats";
import { PortalHowItWorks, PortalGrowthPrompt, PortalMoveInThanks } from "../components/portal/PortalJourney";
import { DEFAULT_PERSONA } from "../components/portal/persona";
import { PortalReferralForm } from "../components/portal/PortalReferralForm";
import { PortalPipeline } from "../components/portal/PortalPipeline";
import { PortalReferralDetail } from "../components/portal/PortalReferralDetail";
import { PortalToolkit, PortalHelp } from "../components/portal/PortalToolkit";
import { BRAND } from "../lib/brandConfig";

/**
 * Urban Nest Quest Partner Hub — token-gated (magic link), no login.
 * Corporate / professional / individual partners refer people, track progress, and access shared resources.
 * All data is scoped server-side to the contact's own company.
 */
export default function PartnerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 403 ? "invalid" : "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    setFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center" data-testid="portal-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8A96B]" />
      </div>
    );
  }

  if (error === "invalid" || (!data && error)) {
    const invalid = error === "invalid";
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center" data-testid={invalid ? "portal-invalid" : "portal-error"}>
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#0A2342]">{invalid ? "Portal link invalid" : "We couldn't load your hub"}</h1>
          <p className="text-sm text-slate-500 mt-2">
            {invalid
              ? "This Partner Hub link is invalid or has been reset. Contact Bryan Spann at (936) 224-1484 for a fresh link."
              : "Something went wrong on our side. Please try again in a moment."}
          </p>
          {!invalid && (
            <button onClick={() => { setLoading(true); load(); }} className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A2342] text-white text-sm font-semibold" data-testid="portal-retry-btn">
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  const { contact, company, stats, referrals, documents } = data;
  const persona = data.persona || DEFAULT_PERSONA;
  const movedIn = referrals.filter((r) => r.lease_signed || r.status === "signed").map((r) => r.referral_name);

  return (
    <div className="min-h-screen bg-[#FAFAF7] pb-24 md:pb-10" data-testid="partner-portal-page">
      <PortalHero contact={contact} company={company} persona={persona} onRefer={openForm} />
      <PortalStats stats={stats} />

      <main className="max-w-5xl mx-auto px-5 py-8 space-y-6">
        <div ref={formRef} className="scroll-mt-4">
          {formOpen && (
            <section className="bg-white rounded-2xl border-2 border-[#C8A96B]/60 shadow-sm" data-testid="portal-referral-form-section">
              <PortalReferralForm
                token={token}
                persona={persona}
                onSubmitted={load}
                onClose={() => setFormOpen(false)}
                onViewReferral={(id) => { setDetailId(id); }}
              />
            </section>
          )}
        </div>

        <PortalPipeline referrals={referrals} stats={stats} persona={persona} onRefer={openForm} onOpen={setDetailId} />
        {movedIn.length > 0 && <PortalMoveInThanks names={movedIn} persona={persona} onRefer={openForm} />}
        {referrals.length > 0 && <PortalGrowthPrompt persona={persona} onRefer={openForm} />}
        <PortalHowItWorks persona={persona} />
        <PortalToolkit documents={documents} />
        <PortalHelp persona={persona} />

        <footer className="text-center text-xs text-slate-400 pt-2">
          Urban Nest Quest | {BRAND.brokerage} · Your private Partner Hub
        </footer>
      </main>

      {/* Sticky mobile CTA */}
      {!formOpen && (
        <div className="md:hidden fixed bottom-0 inset-x-0 p-3 bg-white/95 backdrop-blur border-t border-slate-200 z-40" data-testid="portal-sticky-cta">
          <button onClick={openForm} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#0A2342] text-white font-bold">
            <Plus className="w-5 h-5" /> {persona.cta}
          </button>
        </div>
      )}

      <PortalReferralDetail token={token} referralId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
